import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

/**
 * 404 binnen de app.
 *
 * Nginx serveert `public/404.html` voor paden die de app nooit bereiken, maar de
 * SPA-prefixen vangen alles af: `location ^~ /coach` valt terug op `index.html`, dus
 * `/coach/asdf` komt hier binnen. Zonder deze route matchte er geen enkele `<Route>`
 * en bleef het scherm wit.
 *
 * Tekst en vormgeving volgen bewust `public/404.html`, zodat beide varianten er hetzelfde
 * uitzien. Verschil: wie is ingelogd, gaat terug naar het dashboard in plaats van naar de
 * publieke homepage.
 */
const NotFound: React.FC = () => {
  const { t } = useTranslation('auth');
  const { isAuthenticated, loading } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 pb-nav-space md:pb-6">
      <div className="text-center max-w-md">
        <p className="text-6xl font-bold text-hockey-blue">404</p>
        <h1 className="text-2xl font-semibold text-hockey-brown mt-2 mb-4">
          {t('notFound.title')}
        </h1>
        <p className="text-gray-600 leading-relaxed mb-8">
          {t('notFound.body')}
        </p>

        {/* Vaste hoogte, zodat de knop niet verspringt zodra de inlogstatus bekend is.
            De homepage staat buiten de router (statische HTML), vandaar een `a` en
            geen `Link`. */}
        <div className="h-12 flex items-center justify-center">
          {loading ? null : isAuthenticated ? (
            <Link
              to="/coach"
              className="inline-block bg-hockey-blue hover:bg-hockey-blue-hover text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              {t('notFound.toDashboard')}
            </Link>
          ) : (
            <a
              href="/"
              className="inline-block bg-hockey-blue hover:bg-hockey-blue-hover text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              {t('notFound.toHomepage')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotFound;
